import { type CountryCode } from '@constants'
import { type ProductByProjectCode } from '@hooks-api/types/apiRequest'
import { type Nullable } from '@hooks-api/types/nullable'
import { parseToNumber } from '@utils'
import { useCallback } from 'react'
import { parseProductMap } from '../parser/parseProductMap'
import { type CartItem } from '../types'
import { useCampaignProductsInfoByProjectCode } from './useCampaignProductsInfoByProjectCode'
import { useFpCampaignCartService } from './useFpCampaignCartService'
import { useFpCampaignAllItems } from './useFpCampaignCartStore'

interface UseFpCampaignProductsInfoProps {
  campaignId: number
  countryCode: CountryCode
  campaignCode: Nullable<string>
  extraProductIds?: ProductByProjectCode[]
}

export const useFpCampaignProductsInfo = (props: UseFpCampaignProductsInfoProps) => {
  const { campaignId, countryCode, campaignCode, extraProductIds } = props
  const cartItems = useFpCampaignAllItems(campaignId)
  const { upsertCartItem } = useFpCampaignCartService(campaignId)
  const { productMap, revalidateProductsInfo, getProductsInfo, isProductsLoading } = useCampaignProductsInfoByProjectCode({
    cartItems,
    countryCode,
    campaignCode,
    extraProductIds,
  })

  const getCartItemsStatus = useCallback(async () => {
    const cartItemMap = cartItems.reduce<Record<CartItem['cartItemId'], CartItem>>((acc, item) => {
      acc[item.cartItemId] = item
      return acc
    }, {})
    const productsInfo = await getProductsInfo()
    const hasSoldOutProduct = productsInfo.some(item => parseToNumber(item.stock) <= 0)
    const hasDeficientProduct = productsInfo.some(item => item.stock && parseToNumber(item.stock) < parseToNumber(cartItemMap[item.id]?.quantity || 0))
    return {
      hasSoldOutProduct,
      hasDeficientProduct,
    }
  }, [getProductsInfo, cartItems])

  /**
   * 情境：
   * 如果加進購物車的商品結帳前發現庫存不足，自動調整為上限
   */
  const adjustAllCartItemQuantityWithDeficientProduct = useCallback(async () => {
    const productsInfo = await getProductsInfo()
    const productMap = parseProductMap(productsInfo)
    cartItems.forEach((cartItem) => {
      const productStock = parseToNumber(productMap?.[cartItem.cartItemId]?.stock || 0)
      if (productStock > 0 && productStock < parseToNumber(cartItem.quantity)) {
        upsertCartItem({
          cartItemId: cartItem.cartItemId,
          quantity: productStock,
        })
      }
    })
  }, [cartItems, getProductsInfo, upsertCartItem])

  return {
    productMap,
    revalidateProductsInfo,
    getProductsInfo,
    getCartItemsStatus,
    isProductsLoading,
    adjustAllCartItemQuantityWithDeficientProduct,
  }
}
